import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";

const UploadZone = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: isDragActive ? theme.palette.action.hover : "transparent",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const UploadDialog = ({ 
  open, 
  onClose, 
  onFilesSelected, 
  uploadingFiles, 
  uploadProgress 
}) => {
  const onDrop = useCallback((acceptedFiles) => {
    const audioFiles = acceptedFiles.filter((file) =>
      file.type.startsWith("audio/")
    );
    
    if (audioFiles.length === 0) {
      alert("Please select audio files only");
      return;
    }

    if (onFilesSelected) {
      onFilesSelected(audioFiles);
    }
  }, [onFilesSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "audio/*": [".mp3", ".wav", ".ogg", ".m4a", ".aac", ".flac"],
    },
    multiple: true,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Upload Audio Files
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <UploadZone {...getRootProps()} isDragActive={isDragActive}>
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {isDragActive ? "Drop your audio files here" : "Drag & drop audio files"}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Supports: MP3, WAV, OGG, M4A, AAC, FLAC
          </Typography>
          <Button
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Browse Files
          </Button>
        </UploadZone>

        {/* Upload Progress */}
        {uploadingFiles && uploadingFiles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Uploading Files...
            </Typography>
            {uploadingFiles.map((file) => (
              <Box key={file.name} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" noWrap sx={{ flex: 1, mr: 2 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="body2">
                    {uploadProgress[file.name] || 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress[file.name] || 0}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          {uploadingFiles && uploadingFiles.length > 0 ? "Close (uploads continue in background)" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;